import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Eye, EyeOff, AtSign, Globe, Check, X as XIcon, ChevronDown } from "lucide-react";
import { Logo } from "../components/Logo";
import { Spinner } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { track } from "../lib/analytics";
import { GoogleButton, Field } from "./Login";
import { COUNTRIES, flagEmoji } from "../lib/countries";

const Rule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-600" : "text-muted-foreground"}`} data-testid={`pw-rule-${ok ? "ok" : "no"}`}>
    {ok ? <Check size={13} /> : <XIcon size={13} />}
    <span>{label}</span>
  </div>
);

export default function Signup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const { register } = useAuth();
  const [form, setForm] = useState({
    full_name: "", username: "", email: "",
    dialIso: "US", phone: "", country: "", password: "", confirm: "",
  });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const hasLen = form.password.length >= 8;
  const hasSpecial = /[@#$!%&]/.test(form.password);
  const matches = form.confirm.length > 0 && form.password === form.confirm;
  const dial = COUNTRIES.find((c) => c.iso2 === form.dialIso)?.dial || "+1";

  const submit = async (e) => {
    e.preventDefault();
    if (!hasLen) { toast.error("Password must be at least 8 characters"); return; }
    if (!hasSpecial) { toast.error("Password must include a special character (@ # $ ! % &)"); return; }
    if (!matches) { toast.error("Passwords do not match"); return; }
    if (!form.country) { toast.error("Please select your country"); return; }
    if (!agreed) { toast.error("Please accept the Terms and Privacy Policy"); return; }
    setBusy(true);
    try {
      await register({
        full_name: form.full_name,
        username: form.username,
        email: form.email,
        phone: `${dial} ${form.phone}`.trim(),
        country: form.country,
        password: form.password,
        referral_code: refCode || null,
      });
      track("signup", "/signup");
      toast.success("Account created");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const selectCls = "h-14 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none transition-colors text-[15px] appearance-none";

  return (
    <div className="min-h-screen w-full flex justify-center bg-background">
      <div className="w-full max-w-md bg-mesh min-h-screen px-6 pt-10 pb-10 flex flex-col">
        <Logo size={36} />
        <div className="mt-8 animate-fade-up">
          <h1 className="font-display text-4xl font-light tracking-tighter">Create account</h1>
          <p className="text-muted-foreground mt-2">You'll get a unique Cash Tag to receive money.</p>
          {refCode && (
            <div data-testid="referral-applied" className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/10 text-success text-xs font-semibold">
              <Check size={13} /> Invite code {refCode} applied — you'll earn a bonus
            </div>
          )}
        </div>

        <form onSubmit={submit} className="mt-8 space-y-3">
          <Field icon={User} required placeholder="Full name" data-testid="signup-name-input" value={form.full_name} onChange={set("full_name")} />
          <Field icon={AtSign} required placeholder="Username" data-testid="signup-username-input" value={form.username} onChange={set("username")} />
          <Field icon={Mail} type="email" required placeholder="Email address" data-testid="signup-email-input" value={form.email} onChange={set("email")} />

          {/* Phone: unified field — flag + dial code on the left, number on the right */}
          <div className="flex items-stretch h-14 rounded-2xl bg-secondary border border-transparent focus-within:border-primary focus-within:bg-card transition-colors">
            <div className="relative flex items-center shrink-0">
              <select data-testid="signup-dial-select" value={form.dialIso} onChange={set("dialIso")}
                className="h-full pl-4 pr-7 bg-transparent appearance-none outline-none text-[15px] font-medium cursor-pointer" aria-label="Country code">
                {COUNTRIES.map((c) => (
                  <option key={`d-${c.iso2}`} value={c.iso2}>{flagEmoji(c.iso2)} {c.dial}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2 text-muted-foreground pointer-events-none" />
            </div>
            <div className="w-px bg-border/70 my-3" />
            <input type="tel" required placeholder="Phone number" data-testid="signup-phone-input"
              value={form.phone} onChange={set("phone")}
              className="flex-1 min-w-0 h-full px-4 bg-transparent outline-none text-[15px] rounded-r-2xl" />
          </div>

          {/* Country of residence with flag */}
          <div className="relative">
            <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
            <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select required data-testid="signup-country-select" value={form.country} onChange={set("country")}
              className={`${selectCls} w-full pl-12 pr-10 ${form.country ? "" : "text-muted-foreground"}`}>
              <option value="">Country you live in</option>
              {COUNTRIES.map((c) => (
                <option key={`c-${c.iso2}`} value={c.name} className="text-foreground">{flagEmoji(c.iso2)} {c.name}</option>
              ))}
            </select>
          </div>

          <Field icon={Lock} type={show ? "text" : "password"} required placeholder="Password" data-testid="signup-password-input"
            value={form.password} onChange={set("password")}
            right={<button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>} />

          <Field icon={Lock} type={show ? "text" : "password"} required placeholder="Confirm password" data-testid="signup-confirm-input"
            value={form.confirm} onChange={set("confirm")} />

          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 pb-1">
            <Rule ok={hasLen} label="At least 8 characters" />
            <Rule ok={hasSpecial} label="One of @ # $ ! % &" />
            {form.confirm.length > 0 && (
              <div className={`flex items-center gap-1.5 text-xs ${matches ? "text-emerald-600" : "text-red-500"}`} data-testid="pw-match-status">
                {matches ? <Check size={13} /> : <XIcon size={13} />}
                <span>{matches ? "Passwords match" : "Passwords do not match"}</span>
              </div>
            )}
          </div>

          <label className="flex items-start gap-2.5 pt-1 cursor-pointer select-none">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              data-testid="signup-terms-checkbox"
              className="w-4 h-4 mt-0.5 rounded border-border accent-primary cursor-pointer shrink-0" />
            <span className="text-xs text-muted-foreground leading-snug">
              I agree to the <Link to="/legal/terms" target="_blank" className="text-primary font-semibold">Terms of Service</Link> and <Link to="/legal/privacy" target="_blank" className="text-primary font-semibold">Privacy Policy</Link>.
            </span>
          </label>

          <button type="submit" disabled={busy} data-testid="signup-submit-btn"
            className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(75,35,204,0.3)] transition-transform duration-200 active:scale-[0.98] disabled:opacity-60">
            {busy ? <Spinner className="w-5 h-5" /> : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-4 my-6">
          <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">OR</span><div className="h-px bg-border flex-1" />
        </div>
        <GoogleButton />

        <p className="text-center text-sm text-muted-foreground mt-auto pt-8">
          Already have an account? <Link to="/login" data-testid="go-login-link" className="text-primary font-semibold">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
